// @ts-nocheck
import { OmniStyle, OmniIcon, Color, html, css } from 'omni-ui';
import { OmniElementNext } from 'omni-ui/omni-element-next.js';
import { closestDeep } from '../helpers/util.js';
import 'omni-campaign/src/components/grid-layout/grid-banner.js';
import 'omni-campaign/src/components/empathy-map.js';
import 'omni-campaign/src/components/donut-chart.js';
import {
  strategicDataSample,
  campaignDataSample,
} from './custom-themes-mock-data.js';

OmniStyle.register();
OmniIcon.register();

/**
 * Class for Custom Themes Preview Element
 * Contains preview of theming for a variety of campaign components
 * @extends OmniElementNext
 */
export default class CustomThemesPreview extends OmniElementNext {
  /**
   * Custom Themes Preview Lit Properties
   * @property {Object} theme - Stores the theme that omni-ui > omni-theme uses to apply themes; key is variable name, value is RGB value
   */
  static get properties() {
    return { theme: { type: Object } };
  }

  /**
   * Custom Themes Preview styles
   */
  static get styles() {
    return [
      OmniElementNext.styles,
      css`
        omni-tile {
          margin-bottom: 2.143rem;
        }
        .title-wrapper {
          margin-left: 2.286rem;
        }
        omni-tile.module-wrapper::part(body-slot) {
          padding: 2.143rem 2.857rem 1.429rem 0;
        }
        .preview-elements {
          margin: 2.821rem 0.857rem 1.714rem 0.75rem;
          display: flex;
          flex-direction: column;
          gap: 5.714rem;
        }
        omni-toolbar .title1 {
          box-shadow: 0 0.21rem 0 0 var(--color-accent-one);
          padding-bottom: 0.36rem;
          min-width: 6.43rem;
        }
        omni-toolbar .title2 {
          box-shadow: 0 0.21rem 0 0 var(--color-accent-two);
          padding-bottom: 0.36rem;
          min-width: 6.43rem;
        }
        omni-toolbar .title3 {
          box-shadow: 0 0.21rem 0 0 var(--color-accent-three);
          padding-bottom: 0.36rem;
          min-width: 6.43rem;
        }
        omni-toolbar .title4 {
          box-shadow: 0 0.21rem 0 0 var(--color-accent-four);
          padding-bottom: 0.36rem;
          min-width: 6.43rem;
        }
        div.background-preview {
          background: var(--color-primary);
        }
        .stage-labels {
          display: flex;
          gap: 2.143rem;
        }
        .button-container {
          display: flex;
          gap: 0.714rem;
        }
        .form-fields {
          display: flex;
          gap: 5.686rem;
          justify-content: flex-start;
        }
        .form-fields div {
          flex-grow: 1;
        }
        .field-dropdown {
          max-width: 21.5rem;
          width: 100%;
        }
        .field-text-area {
          max-width: 32.857rem;
          width: 100%;
        }
        .visualizations {
          display: flex;
          gap: 5.686rem;
        }
        .donut-container {
          position: relative;
          flex-grow: 1;
          max-width: 400px;
          width: 100%;
        }
        .component-container {
          display: flex;
          flex-direction: column;
          gap: 1.429rem;
        }
        .upper-preview {
          display: flex;
          gap: 5.714rem;
        }
        .upper-preview-right {
          display: flex;
          flex-direction: column;
          gap: 4.286rem;
        }
        .upper-preview-right-outer {
          display: flex;
          gap: 5.714rem;
        }
        .upper-preview-right-outer-left {
          display: flex;
          flex-direction: column;
          gap: 4.286rem;
        }
        left-nav {
          display: flex;
          flex: 1 1 auto;
        }
        empathy-map {
          flex-grow: 1;
        }
        @media (max-width: 1105px) {
          .visualizations {
            flex-wrap: wrap;
          }
          .omni campaign-donut-chart svg {
            min-width: none !important;
          }
          .upper-preview-right-outer {
            flex-direction: column;
          }
          .stage-labels {
            flex-wrap: wrap;
          }
        }
        @media (max-width: 805px) {
          .upper-preview {
            flex-direction: column;
          }
        }
      `,
    ];
  }

  connectedCallback() {
    super.connectedCallback();
    // Store the omni-theme element that exists in app-layout so we can reset the theme when this component is disconnected.
    const appLayout = closestDeep('app-layout', this);
    this.omniTheme = appLayout.shadowRoot.querySelector('omni-theme');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.omniTheme.dispatchNewEvent('omni-theme', { detail: null });
  }

  /**
   * @description Applies theme colors by dispatching omni-theme event
   */
  updated() {
    this.omniTheme.dispatchNewEvent('omni-theme', { detail: this.#v3theme });
  }

  /**
   * Given a hex color (used as the base/--color-primary), return the three colors needed for omni-ui's accessible color theming
   * @param {string} - hexColorPrimary - "--color-primary" hex string value,  include the preceding `#` in the string
   * @returns{{
   *  '--color-primary': string,
   *  '--color-primary-hover': string
   *  '--color-primary-pressed': string,
   * }}
   *
   * Copied from omni-campaign, should probably live in omni-ui's theming logic instead.
   */
  #calcPrimaryHoverAndPrimaryPressed = hexColorPrimary => {
    const themeBaseColorHSL = new Color(hexColorPrimary).toHSL();
    const clampedLightnessHover = Math.max(
      0,
      Math.min(100, themeBaseColorHSL.l * 100 - 4)
    );
    const clampedLightnessPressed = Math.max(
      0,
      Math.min(100, themeBaseColorHSL.l * 100 - 8)
    );
    const themeBaseColorHoverHSL = `hsl(${themeBaseColorHSL.h}, ${themeBaseColorHSL.s * 100}%, ${clampedLightnessHover}%)`;
    const themeBaseColorPressedHSL = `hsl(${themeBaseColorHSL.h}, ${themeBaseColorHSL.s * 100}%, ${clampedLightnessPressed}%)`;
    const themeBaseColorHoverHex = new Color(themeBaseColorHoverHSL).hex;
    const themeBaseColorPressedHex = new Color(themeBaseColorPressedHSL).hex;
    return {
      '--color-primary': hexColorPrimary,
      '--color-primary-hover': themeBaseColorHoverHex,
      '--color-primary-pressed': themeBaseColorPressedHex,
    };
  };

  /**
   * @description Adds additional variables to the theme object. Without this logic
   * buttons remain unthemed despite --color-primary being set.
   *
   * Copied from omni-campaign, should probably live in omni-ui's theming logic instead.
   */
  get #v3theme() {
    if (!Object.keys(this.theme ?? {}).length) return this.defaultTheme;
    if (!this.theme['--rgb-primary']) return this.theme;

    const [r, g, b] = this.theme['--rgb-primary']
      .split(',')
      .map(c => parseInt(c, 10));
    const hexColorFromRgb = new Color({ r, g, b }).hex;
    return {
      ...this.theme,
      ...this.#calcPrimaryHoverAndPrimaryPressed(hexColorFromRgb),
    };
  }

  /**
   * @returns {html} Rendered HTML for the component
   */
  render() {
    return html` <omni-style>
      <omni-tile class="module-wrapper themed">
        <div class="title-wrapper">
          <h1 class="title mb-0 is-2">Theme Preview</h1>
          <div class="preview-elements">
            <div class="upper-preview">
              <div class="component-container">
                <h1
                  class="title is-size-6 is-clamped has-text-grey-dark is-uppercase mb-0"
                >
                  Left Navigation
                </h1>
                <left-nav class="themed"></left-nav>
              </div>
              <div class="upper-preview-right">
                <div class="upper-preview-right-outer">
                  <div class="upper-preview-right-outer-left">
                    <div class="component-container">
                      <h1
                        class="title is-size-6 is-clamped has-text-grey-dark is-uppercase mb-0"
                      >
                        Tabs
                      </h1>
                      <div>
                        <button class="tab active">Workflow</button>
                      </div>
                    </div>
                    <div class="component-container">
                      <h1
                        class="title is-size-6 is-clamped has-text-grey-dark is-uppercase mb-0"
                      >
                        Buttons
                      </h1>
                      <div class="button-container">
                        <button>Primary</button>
                        <button class="secondary">Secondary</button>
                        <button class="tertiary">Tertiary</button>
                      </div>
                    </div>
                  </div>
                  <div class="component-container">
                    <h1
                      class="title is-size-6 is-clamped has-text-grey-dark is-uppercase mb-0"
                    >
                      Step Card
                    </h1>
                    <step-card class="themed"></step-card>
                  </div>
                </div>
                <div class="component-container">
                  <h1
                    class="title is-size-6 is-clamped has-text-grey-dark is-uppercase mb-0"
                  >
                    Stages
                  </h1>
                  <omni-toolbar class="themed" slot="subheader">
                    <div class="stage-labels">
                      <h1 class="title1 is-3">Ambition</h1>
                      <h1 class="title2 is-3">Empathy</h1>
                      <h1 class="title3 is-3">Craft</h1>
                      <h1 class="title4 is-3">Activate</h1>
                    </div>
                  </omni-toolbar>
                </div>
              </div>
            </div>
            <campaign-grid-banner
              .campaignSummary=${campaignDataSample}
              .readonly=${true}
              class="themed"
            ></campaign-grid-banner>
            <div class="component-container">
              <h1
                class="title is-size-6 is-clamped has-text-grey-dark is-uppercase mb-0"
              >
                Form Fields
              </h1>
              <div class="form-fields">
                <div class="field-text-area">
                  <label class="subtitle mr-1 mb-2 text-area-label"
                    >Text area</label
                  >
                  <textarea
                    class="textarea"
                    placeholder="Text area text"
                  ></textarea>
                </div>
                <div class="mb-5 field-dropdown">
                  <label class="subtitle mr-1 mb-2 dropdown-label"
                    >Dropdown</label
                  >
                  <div>
                    <om2-dropdown
                      class="themed"
                      placeholder="Select Option"
                      .options=${['test1', 'test2']}
                      @change=${e => {
                        e.preventDefault();
                      }}
                    ></om2-dropdown>
                  </div>
                </div>
              </div>
            </div>
            <div class="component-container">
              <h1
                class="title is-size-6 is-clamped has-text-grey-dark is-uppercase mb-0"
              >
                Visualizations
              </h1>
              <div class="visualizations">
                <div class="donut-container">
                  <campaign-donut-chart
                    .strategicData=${strategicDataSample}
                    class="themed"
                  >
                  </campaign-donut-chart>
                </div>
                <empathy-map></empathy-map>
              </div>
            </div>
          </div>
        </div>
      </omni-tile>
    </omni-style>`;
  }
}

customElements.define('custom-themes-preview', CustomThemesPreview);
